import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { RippleModule } from 'primeng/ripple';
import { map, timer } from 'rxjs';
import { IconKey } from '../../../../../../whitelabel/icon-key.enum';
import { OverlayPanelComponent } from '../../../../../osp-ui/components/overlay/overlay-panel/overlay-panel.component';
import { AlarmNotification } from '../../../../../pages/notifications/contract/alarm-notification.contract';
import { Alarm } from '../../../../../pages/notifications/contract/alarm.contract';
import { AlarmType } from '../../../../../pages/notifications/enums/alarm-type.enum';
import { AlarmTypeResolverPipe } from '../../../../../pages/notifications/pipes/alarm-type-resolver.pipe';
import { NotificationsDatasourceService } from '../../../../../pages/notifications/services/notifications-datasource.service';
import { AlarmLevelToNotificationBallSeverityPipe } from '../../../../../shared/pipes/alarm-level-to-notification-ball-severity.pipe';
import { IconPipe } from '../../../../../shared/pipes/icon.pipe';
import { AuthService } from '../../../../services/auth.service';
import { LanguageService } from '../../../../services/language.service';
import { AcknowledgeNotificationButtonComponent } from '../shared/acknowledge-notification-button/acknowledge-notification-button.component';
import { NotificationOverlayTogglerComponent } from '../shared/notification-overlay-toggler/notification-overlay-toggler.component';
import { NotificationSeverityBallComponent } from '../shared/notification-severity-ball/notification-severity-ball.component';
import { OverlayNotificationComponent } from '../shared/overlay-notification/overlay-notification.component';

@Component({
  selector: 'osp-notifications-center-overlay',
  standalone: true,
  imports: [
    OverlayPanelComponent,
    CommonModule,
    RippleModule,
    AlarmTypeResolverPipe,
    TranslateModule,
    IconPipe,
    NotificationSeverityBallComponent,
    AlarmLevelToNotificationBallSeverityPipe,
    AcknowledgeNotificationButtonComponent,
    OverlayNotificationComponent,
    NotificationOverlayTogglerComponent,
  ],
  templateUrl: './notifications-center-overlay.component.html',
  styleUrls: ['./notifications-center-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsCenterOverlayComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private notificationService = inject(NotificationsDatasourceService);
  private authService = inject(AuthService);
  private languageService = inject(LanguageService);

  @ViewChild('op') overlayPanel!: OverlayPanelComponent;

  private FETCH_ALARMS_INTERVAL_MS = 30000;

  private readonly jumpToEquipmentText = this.translate('general.labels.jumpTo', {
    value: this.translate('general.equipm.s'),
  });
  private readonly jumpToTransportText = this.translate('general.labels.jumpTo', {
    value: this.translate('general.transport'),
  });
  private readonly jumpToProjectText = this.translate('general.labels.jumpTo', {
    value: this.translate('general.project.s'),
  });

  notifications$ = this.notificationService.currentActive$.pipe(
    takeUntilDestroyed(this.destroyRef),
    map((warnings) => this.alarmsToNotifications(warnings)),
  );

  notificationsTotalAmount$ = this.notificationService.currentActiveTotalCount$.pipe(
    takeUntilDestroyed(this.destroyRef),
  );

  ngOnInit(): void {
    this.getAlarms();
  }

  onItemClick(url: string): void {
    this.overlayPanel.hide();
    this.router.navigate([url]);
  }

  private isGeoFenceAlarm(alarm: Alarm): boolean {
    return alarm.alarmType === AlarmType.GEOFENCE_VIOLATION;
  }

  public acknowledgeAlarm(id: string): void {
    this.notificationService
      .acknowledgeAlarm({ id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.notificationService.getCurrentActive();
      });
  }

  private getAlarms() {
    timer(0, this.FETCH_ALARMS_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.authService.isTokenExpired()) {
          this.notificationService.getCurrentActive();
        }
      });
  }

  private alarmsToNotifications(alarms: Alarm[]): AlarmNotification[] {
    return alarms.map((alarm) => {
      let alarmNotification;

      switch (alarm.alarmType) {
        case AlarmType.TRANSPORT_TASK_CREATION:
        case AlarmType.TRANSPORT_TASK_RETURNED:
          alarmNotification = this.transportAlarmToNotification(alarm);
          break;

        case AlarmType.THEFT_PROTECTION:
          alarmNotification = this.theftProtectionAlarmToNotification(alarm);
          break;

        default:
          alarmNotification = this.equipmentAlarmToNotification(alarm);
          break;
      }

      if (this.isGeoFenceAlarm(alarm) && alarm.projectNumber && alarm.projectName) {
        alarmNotification.projectName = alarm.projectName;
        alarmNotification.projectNumber = alarm.projectNumber;
      }
      return alarmNotification;
    });
  }

  private equipmentAlarmToNotification(alarm: Alarm): AlarmNotification {
    const equipmentNameOrModel = alarm.equipmentName || alarm.equipmentModel || '';
    const serialNumber = alarm.equipmentCustomerSerialNumber ? `(${alarm.equipmentCustomerSerialNumber})` : '';
    const title = `${equipmentNameOrModel} ${serialNumber}`;

    return {
      alarmId: alarm.alarmId,
      alarmRaisedDate: new Date(alarm.alarmRaisedDate),
      title,
      message: alarm.alarmMessage,
      alarmLevel: alarm.alarmLevel,
      alarmType: alarm.alarmType,
      navigateUrl: alarm.entityId ? `/resources/equipment/${alarm?.entityId}/general-data/base-data` : undefined,
      navigateUrlTooltip: alarm.entityId ? this.jumpToEquipmentText : undefined,
      navigateUrlSubtitle: alarm.projectId ? `sites/projects/list/${alarm.projectId}/general` : undefined,
      navigateUrlSubtitleTooltip: alarm.projectId ? this.jumpToProjectText : undefined,
    };
  }

  private transportAlarmToNotification(alarm: Alarm): AlarmNotification {
    return {
      alarmId: alarm.alarmId,
      alarmRaisedDate: new Date(alarm.alarmRaisedDate),
      title: alarm.transportTitle ?? '',
      message: alarm.alarmMessage,
      alarmLevel: alarm.alarmLevel,
      alarmType: alarm.alarmType,
      navigateUrl: alarm.entityId ? `transportation/list/${alarm.entityId}` : undefined,
      navigateUrlTooltip: alarm.entityId ? this.jumpToTransportText : undefined,
      navigateUrlSubtitle: undefined,
      navigateUrlSubtitleTooltip: undefined,
    };
  }

  private theftProtectionAlarmToNotification(alarm: Alarm): AlarmNotification {
    return {
      alarmId: alarm.alarmId,
      alarmRaisedDate: new Date(alarm.alarmRaisedDate),
      title:
        `${alarm.equipmentName || alarm.equipmentModel}` +
        ` ${alarm.equipmentCustomerSerialNumber ? `(${alarm.equipmentCustomerSerialNumber})` : ''}`,
      message: alarm.alarmMessage,
      alarmLevel: alarm.alarmLevel,
      alarmType: alarm.alarmType,
      navigateUrl: !alarm.acknowledged
        ? `/theft-protection-alarm/${alarm.alarmId}`
        : `/resources/equipment/${alarm?.entityId}/general-data/base-data`,
      shouldHideDefaultAcknowledgeButton: true,
    };
  }

  private translate(key: string, interpolateParams?: object): string {
    return this.languageService.getInstant(key, interpolateParams);
  }

  protected readonly IconKey = IconKey;
}
